// Requêtes des écrans des conseils (lot L5-B, M16, TanStack Query), clés sous « conseils ». Types de
// packages/contrats/src/conseils.ts ; le serveur refait tous les contrôles (quorum, mode de vote, états).
import type {
  Accuse,
  CreationMandatElu,
  CreationPoint,
  CreationRegle,
  CreationSession,
  FeuillePresence,
  MandatElu,
  RegleConseil,
  ResumeSession,
  SessionConseil,
  TexteDeliberation,
  Transmission,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface Liste<T> {
  elements: T[];
}

export const INVALIDER_CONSEILS: readonly (readonly unknown[])[] = [['conseils'], ['indicateurs']];

const lire = <T,>(chemin: string) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal });
const envoyer = <T,>(methode: 'POST' | 'PUT', chemin: string, corps?: unknown) => requete<T>(chemin, { methode, corps });

export const useSessions = () => useQuery({ queryKey: ['conseils', 'sessions'], queryFn: lire<Liste<ResumeSession>>('/conseils/sessions'), retry: false });
export const useSession = (id: string | undefined) => useQuery({ queryKey: ['conseils', 'session', id ?? ''], queryFn: lire<SessionConseil>(`/conseils/sessions/${id}`), enabled: Boolean(id), retry: false });
export const useMandats = () => useQuery({ queryKey: ['conseils', 'elus'], queryFn: lire<Liste<MandatElu>>('/conseils/elus'), retry: false });
export const useRegles = () => useQuery({ queryKey: ['conseils', 'regles'], queryFn: lire<Liste<RegleConseil>>('/conseils/regles'), retry: false });

export const creerSession = (c: CreationSession) => envoyer<SessionConseil>('POST', '/conseils/sessions', c);
export const creerMandat = (c: CreationMandatElu) => envoyer<MandatElu>('POST', '/conseils/elus', c);
export const finirMandat = (id: string, c: { fin: string; motif: string }) => envoyer<MandatElu>('POST', `/conseils/elus/${id}/fin`, c);
export const creerRegle = (c: CreationRegle) => envoyer<RegleConseil>('POST', '/conseils/regles', c);
export const validerRegle = (id: string) => envoyer<RegleConseil>('POST', `/conseils/regles/${id}/validation`, {});
export const choisirRegle = (id: string, regleId: string) => envoyer<SessionConseil>('PUT', `/conseils/sessions/${id}/regle`, { regle_id: regleId });
export const ajouterPoint = (id: string, c: CreationPoint) => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/points`, c);
export const saisirPresences = (id: string, c: FeuillePresence) => envoyer<SessionConseil>('PUT', `/conseils/sessions/${id}/presences`, c);
export const designerBureau = (id: string, president: string, secretaire: string) => envoyer<SessionConseil>('PUT', `/conseils/sessions/${id}/bureau`, { president_mandat_id: president, secretaire_mandat_id: secretaire });
export const validerPv = (id: string, qualite: 'president' | 'secretaire') => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/pv/validation`, { qualite });
export const transmettre = (id: string, c: Transmission) => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/transmission`, c);
export const enregistrerAccuse = (id: string, c: Accuse) => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/accuse`, c);
export const sansQuorum = (id: string, motif: string) => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/sans-quorum`, { motif });

/** Actes simples de la session (sans corps) : convocation, ouverture, clôture, PV, publication, archivage. */
export const ACTES_SESSION = {
  convocation: 'Convoquer les élus',
  ouverture: 'Ouvrir la séance',
  cloture: 'Clore la séance',
  pv: 'Générer le procès-verbal',
  publication: 'Publier les délibérations',
  archivage: 'Archiver',
} as const;
export type ActeSession = keyof typeof ACTES_SESSION;
export const acteSession = (id: string, acte: ActeSession) => envoyer<SessionConseil>('POST', `/conseils/sessions/${id}/${acte}`, {});

export const voter = (id: string, corps: unknown) => envoyer<SessionConseil>('POST', `/conseils/deliberations/${id}/vote`, corps);
export const reporter = (id: string, motif: string) => envoyer<SessionConseil>('POST', `/conseils/deliberations/${id}/report`, { motif });
export const modifierTexte = (id: string, c: TexteDeliberation) => envoyer<SessionConseil>('PUT', `/conseils/deliberations/${id}/texte`, c);
export const lierObjet = (id: string, objetType: 'projet_travaux' | 'dossier' | 'budget', objetId: string) => envoyer<SessionConseil>('POST', `/conseils/deliberations/${id}/lien-execution`, { objet_type: objetType, objet_id: objetId });
export const constaterExecution = (id: string, observation: string) => envoyer<SessionConseil>('POST', `/conseils/deliberations/${id}/execution`, { observation });
